import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from '@fieldnation/platform-components';
import css from '../common/ReliabilityScore.scss';

import {
  BackOutsProps,
  ReliabilityScoreProps,
  WorkOrderRowProps,
} from '../common/types/reliabilityScore.type';
import {
  BACKOUT_CONSTANTS as CONSTANTS,
  BACKOUT_SCORE_NOTES,
  BACKOUT_TYPE_CONFIG,
} from '../common/config';
import {
  convertToDate, getTypeClass, adjustTextForTableWidth, isMyOwnProfile,
  isStaff,
} from '../common/utils';
import Badge from '../common/components/Badge';

const BackOutItem: React.FC<ReliabilityScoreProps> = React.memo((props) => {
  const {
    label, count, color, totalAssignments = 0,
  } = props;

  return (
    <div className={css['back-out-item']}>
      <div className={css['back-out-label']}>{label}</div>
      <div className={css['back-out-bar']}>
        <ProgressBar
          currentProgress={count}
          maxProgress={totalAssignments}
          fillColor={color}
        />
      </div>
      <span className={css['back-out-count']}>{count}</span>
    </div>
  );
});

const WorkOrderRow: React.FC<WorkOrderRowProps> = React.memo(({ item }) => {
  const formattedDate = useMemo(
    () => convertToDate(item?.cancelledAt?.utc),
    [item?.cancelledAt?.utc],
  );

  const typeClass = getTypeClass(item?.type);
  const badgeClassName = `${css['type-badge']} ${css[typeClass]}`;

  return (
    <tr>
      <td>{formattedDate}</td>
      <td>{item?.workOrderId}</td>
      <td>
        <Badge type={item?.type} classType={badgeClassName} />
      </td>
      <td>{adjustTextForTableWidth(item?.title, 200)}</td>
    </tr>
  );
});

const BackoutDescription: React.FC = () => (
  <div className={css['back-out-description']}>
    {Object.values(BACKOUT_TYPE_CONFIG).map(({ label, description }) => (
      <p>
        <span className={css['bold-label']}>
          {label}
          :
        </span>
        {' '}
        {description}
      </p>
    ))}
    <p>
      <span className={css['bold-label']}>
        Note:
      </span>
      {' '}
      {BACKOUT_SCORE_NOTES}
    </p>
  </div>
);

const BackOutScore: React.FC<BackOutsProps> = (props) => {
  const { workOrderData, providerId } = props;
  const totalAssignments = useMemo(
    () => workOrderData?.details?.totalAssignment ?? 0,
    [workOrderData?.details?.totalAssignment],
  );

  const cancellationCounts = useMemo(() => ({
    early: workOrderData?.details?.earlyCancellation ?? 0,
    late: workOrderData?.details?.lateCancellation ?? 0,
    noShow: workOrderData?.details?.noShow ?? 0,
    fulfilled: workOrderData?.details?.totalFulfilled ?? workOrderData?.details?.totalAssignment ?? 0,
  }), [
    workOrderData?.details?.earlyCancellation,
    workOrderData?.details?.lateCancellation,
    workOrderData?.details?.noShow,
    workOrderData?.details?.totalFulfilled,
    workOrderData?.details?.totalAssignment,
  ]);

  const workOrders = useMemo(
    () => workOrderData?.details?.cancellationData || [],
    [workOrderData?.details?.cancellationData],
  );

  const isMyProfile = useMemo(() => isMyOwnProfile(providerId), [providerId]);

  const tableVisibility = (isMyProfile || isStaff) && !!workOrders?.length;

  const BackOutTypes = useMemo(() => [
    {
      ...BACKOUT_TYPE_CONFIG.FULFILLED,
      count: cancellationCounts.fulfilled,
    },
    {
      ...BACKOUT_TYPE_CONFIG.EARLY,
      count: cancellationCounts.early,
    },
    {
      ...BACKOUT_TYPE_CONFIG.LATE,
      count: cancellationCounts.late,
    },
    {
      ...BACKOUT_TYPE_CONFIG.NO_CALL_NO_SHOW,
      count: cancellationCounts.noShow,
    },
  ], [cancellationCounts.early, cancellationCounts.late, cancellationCounts.noShow, cancellationCounts.fulfilled]);

  return (
    <>
      <div
        className={css['work-order-cancellations']}
        style={!tableVisibility ? { height: 'auto' } : undefined}
      >
        <div className={css['back-out-section']}>
          <div>
            {BackOutTypes.map((item) => (
              <BackOutItem key={item?.label} {...item} totalAssignments={totalAssignments} />
            ))}
          </div>
          <BackoutDescription />
        </div>

        {tableVisibility && (
        <div className={css['table-container']}>
          <div className={css['table-wrapper']}>
            <table className={css['work-order-table']}>
              <thead>
                <tr>
                  <th>{CONSTANTS.TABLE_HEADERS.DATE}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.WORK_ORDER}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.TYPE}</th>
                  <th>{CONSTANTS.TABLE_HEADERS.WORK_ORDER_TITLE}</th>
                </tr>
              </thead>
              <tbody>
                {workOrders?.map((item) => (
                  <WorkOrderRow key={item?.id} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

      </div>
      {isMyProfile && (
      <div className={css['warning-message']}>
        {CONSTANTS.MESSAGES.WARNING}
        {' '}
        <a href={CONSTANTS.MESSAGES.LINK_URL} target="_blank" className={css['warning-link']} rel="noreferrer">
          {CONSTANTS.MESSAGES.LINK_TEXT}
        </a>
      </div>
      )}
    </>
  );
};

BackOutItem.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  color: PropTypes.oneOf(['green', 'indigo', 'red', 'yellow', 'yellow-light', 'gray', 'orange', 'teal'] as const).isRequired,
  totalAssignments: PropTypes.number,
};

WorkOrderRow.propTypes = {
  item: PropTypes.any.isRequired,
};

BackOutScore.propTypes = {
  workOrderData: PropTypes.any,
  providerId: PropTypes.number.isRequired,
};

BackOutItem.defaultProps = {
  totalAssignments: 0,
};

BackOutScore.defaultProps = {
  workOrderData: undefined,
};

export default React.memo(BackOutScore);
